import { Listing } from '@/components/Listing';
import Page, {
    PageHeader,
    PageHeaderActions,
    PageHeaderContent,
    PageHeaderDescription,
    PageHeaderTitle,
} from '@/components/Page';
import { DeleteQuestionDialog } from '@/components/questions';
import { usePaginationState } from '@/hooks/usePaginationState';
import {
    useCreateQuestion,
    useDeleteQuestion,
    useQuestions,
    type Question,
} from '@/hooks/useQuestions';
import {
    extractTextFromOptionJson,
    extractTextFromTitleJson
} from '@/lib/lexical-utils';
import type { AppContext } from '@/main';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    PaginationState,
    useReactTable,
} from '@tanstack/react-table';
import {
    Badge,
    Item,
    ItemActions,
    ItemContent,
    ItemMedia
} from '@ui/components';
import { Button } from '@ui/components/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@ui/components/dropdown-menu';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@ui/components/hover-card';
import { format } from 'date-fns';
import {
    Check,
    Copy,
    Edit2,
    Hash,
    Info,
    MoreHorizontal,
    Plus,
    Tag,
    Trash2
} from 'lucide-react';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';

export const Route = createFileRoute('/_dashboard/questions/')<AppContext>({
  component: RouteComponent,
  loader: () => ({
    crumb: 'Questions',
  }),
});

function RouteComponent() {
  const navigate = useNavigate();

  const { pagination, setPagination, orderDirection, setOrderDirection, orderBy, setOrderBy } =
    usePaginationState<'createdAt' | 'updatedAt'>({
      defaultOrder: 'createdAt',
    });

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: null as string | null,
    title: '',
  });

  const {
    data: questionsResponse,
    isLoading,
    error,
  } = useQuestions({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    orderBy,
    orderDirection,
  });

  const questions = questionsResponse?.items || [];
  const table = useQuestionsTable({
    items: questions,
    pagination,
    setPagination,
    rowCount: questionsResponse?.totalItems || 0,
  });

  const createQuestionMutation = useCreateQuestion();
  const deleteQuestionMutation = useDeleteQuestion();

  const handleCreateQuestion = async () => {
    const newQuestion = await createQuestionMutation.mutateAsync({});
    navigate({
      to: '/questions/$questionId',
      params: { questionId: newQuestion.id },
    });
  };

  const handleDeleteQuestion = async (questionId: string) => {
    await deleteQuestionMutation.mutateAsync(questionId);
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-destructive">Failed to load questions</div>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <Page>
      <PageHeader>
        <PageHeaderContent>
          <PageHeaderTitle>Questions</PageHeaderTitle>
          <PageHeaderDescription>
            Manage your question bank and content
            {questionsResponse && (
              <span className="ml-2">({questionsResponse.totalItems} total)</span>
            )}
          </PageHeaderDescription>
        </PageHeaderContent>
        <PageHeaderActions>
          <Button onClick={handleCreateQuestion} className="gap-2">
            <Plus className="h-4 w-4" />
            Create Question
          </Button>
        </PageHeaderActions>
      </PageHeader>

      <Listing.Root
        values={questions}
        CardComponent={QuestionCard}
        table={table}
        isLoading={isLoading}
        totalItems={questionsResponse?.totalItems || 0}
        onDelete={(item: Question) => {
          setDeleteDialog({
            open: true,
            id: item.id,
            title: extractTextFromTitleJson(item.activeVersion?.title || ''),
          });
        }}
        onEdit={(item: Question) => {
          navigate({
            to: '/questions/$questionId',
            params: {
              questionId: item.id,
            },
          });
        }}
      >
        <Listing.Header.Root>
          <Listing.Header.StatusFilter
            active="all"
            options={[
              {
                label: 'All Exams',
                value: 'all',
              },
            ]}
            default="all"
            onChange={() => {}}
          />
          <Listing.Header.Selector.Root>
            <Listing.Header.Selector.View />
            <Listing.Header.Selector.OrderBy
              default="createdAt"
              onChange={(value) => {
                const [orderBy, orderDirection] = value.split('-');
                setOrderBy(orderBy as 'createdAt' | 'updatedAt');
                setOrderDirection(orderDirection as 'asc' | 'desc');
              }}
              options={[
                {
                  label: 'Created At',
                  value: 'createdAt',
                },
                {
                  label: 'Updated At',
                  value: 'updateAt',
                },
              ]}
              orderBy={orderBy}
              orderDirection={orderDirection}
            />
            <Listing.Header.Selector.PageSize
              value={pagination.pageSize.toString()}
              onChange={(value) => setPagination({ ...pagination, pageSize: parseInt(value, 10) })}
            />
          </Listing.Header.Selector.Root>
        </Listing.Header.Root>
      </Listing.Root>

      {/* Dialogs */}
      <DeleteQuestionDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog((prev) => ({ ...prev, open }))}
        questionId={deleteDialog.id}
        questionTitle={deleteDialog.title}
        onDelete={handleDeleteQuestion}
      />
    </Page>
  );
}

function QuestionCard({
  item,
  onDelete,
  onEdit,
}: {
  item: Question;
  onDelete: (item: Question) => void;
  onEdit: (item: Question) => void;
}) {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopyId = useCallback(async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      // Clear the feedback after 2 seconds
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  }, []);

  return (
    <Item className="h-fit" variant="outline">
      <ItemContent className="w-full">
        <Link
          className="prose text-base font-medium line-clamp-3 hover:underline"
          to="/questions/$questionId"
          params={{ questionId: item.id }}
        >
          {extractTextFromTitleJson(item.activeVersion?.title) || 'Click to edit'}
        </Link>
      </ItemContent>
      <ItemActions>
        <HoverCard>
          <HoverCardTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <Info className="h-4 w-4" />
            </Button>
          </HoverCardTrigger>
          <HoverCardContent className="min-w-fit">
            <div className="space-y-3">
              <h4 className="text-sm font-semibold">Question Information</h4>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Hash className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Question ID:</span>
                  <button
                    onClick={() => handleCopyId(item.id)}
                    className="font-mono text-xs hover:text-primary cursor-pointer flex items-center gap-1"
                    title="Click to copy"
                  >
                    {item.id}
                    {copiedId === item.id ? (
                      <Copy className="h-3 w-3 text-green-500" />
                    ) : (
                      <Copy className="h-3 w-3 opacity-0 hover:opacity-100 transition-opacity" />
                    )}
                  </button>
                </div>

                <div className="flex items-center gap-2 text-sm">
                  <Tag className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Public ID:</span>
                  <button
                    onClick={() => handleCopyId(item.publicId)}
                    className="font-mono text-xs hover:text-primary cursor-pointer flex items-center gap-1"
                    title="Click to copy"
                  >
                    {item.publicId}
                    {copiedId === item.publicId ? (
                      <Copy className="h-3 w-3 text-green-500" />
                    ) : (
                      <Copy className="h-3 w-3 opacity-0 hover:opacity-100 transition-opacity" />
                    )}
                  </button>
                </div>

                {item.activeVersion && (
                  <div className="flex items-center gap-2 text-sm">
                    <Tag className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Type:</span>
                    <span className="capitalize">{item.activeVersion.type.replace('-', ' ')}</span>
                  </div>
                )}
              </div>
            </div>
          </HoverCardContent>
        </HoverCard>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(item)}>
              <Edit2 className="mr-2 h-4 w-4" />
              Add Version
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onDelete(item)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </ItemActions>

      {item.activeVersion && item.activeVersion.options.length > 0 && (
        <ItemContent className="pt-0 w-full">
          {item.activeVersion.options.map((option, index) => {
            const isCorrect = option.isCorrect || item.activeVersion?.correctAnswer === index;
            return (
              <Item
                key={index}
                className="prose text-sm truncate flex flex-nowrap"
                variant={isCorrect ? 'outline' : 'muted'}
              >
                <ItemMedia>
                  {isCorrect ? (
                    <Badge variant="outline" className="bg-accent size-8">
                      <Check />
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="size-8"></Badge>
                  )}
                </ItemMedia>
                <ItemContent className="line-clamp-2">
                  {extractTextFromOptionJson(option.body)}
                </ItemContent>
              </Item>
            );
          })}
        </ItemContent>
      )}
    </Item>
  );
}

export function useQuestionsTable({
  items,
  pagination,
  setPagination,
  rowCount,
}: {
  items: Question[];
  pagination: PaginationState;
  setPagination: Dispatch<SetStateAction<PaginationState>>;
  rowCount: number;
}) {
  return useReactTable({
    data: items,
    columns: [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 400,
        cell: ({ row: { original } }) => {
          return (
            <div className="flex flex-col gap-1 max-w-sm sm:max-w-md md:max-w-xl lg:max-w-2xl xl:max-w-4xl 2xl:max-w-8xl ">
              <Link
                className="prose text-base font-medium line-clamp-3 hover:underline"
                to="/questions/$questionId"
                params={{ questionId: original.id }}
              >
                <h1 className="text-md font-medium truncate">
                  {extractTextFromTitleJson(original.activeVersion?.title) || 'Click to edit'}
                </h1>
              </Link>
              {original.activeVersion?.options.map((option, index) => {
                const isCorrect =
                  option.isCorrect || original.activeVersion?.correctAnswer === index;
                return (
                  <Item
                    key={index}
                    className="prose text-xs truncate flex flex-nowrap"
                    variant={isCorrect ? 'outline' : 'muted'}
                  >
                    <ItemMedia>
                      {isCorrect ? (
                        <Badge variant="outline" className="bg-accent size-4">
                          <Check />
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="size-4"></Badge>
                      )}
                    </ItemMedia>
                    <ItemContent className="line-clamp-2">
                      {extractTextFromOptionJson(option.body)}
                    </ItemContent>
                  </Item>
                );
              })}
            </div>
          );
        },
      },
      {
        accessorKey: 'archived',
        header: 'Status',
        cell: ({
          row: {
            original: { archived },
          },
        }) => (
          <Badge variant={archived ? 'outline' : 'default'}>
            {archived ? 'Archived' : 'Active'}
          </Badge>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created at',
        cell: ({ row: { original: exam } }) => (
          <div className="text-muted-foreground">
            {format(new Date(exam.createdAt), 'MMM dd, yyyy')}
          </div>
        ),
      },
    ],
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
    manualPagination: true,
    rowCount,
  });
}
